// ConfirmDialog.jsx
import { memo } from 'react';
import {
  Button, Card, CardContent, Dialog, DialogActions,
  DialogContent, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import AddCircleOutlineIcon   from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon        from '@mui/icons-material/ContentCopy';
import ShareIcon              from '@mui/icons-material/Share';

/**
 * Modal de confirmación que se muestra tras guardar o actualizar un survey.
 *
 * Props
 * ─────
 * @param {boolean}  open        – Controla visibilidad del diálogo.
 * @param {Function} onClose     – Callback al cerrar / botón "Ver encuesta".
 * @param {string}   title       – Título del survey guardado.
 * @param {string}   description – Descripción del survey (opcional).
 * @param {boolean}  isEditing   – true = modo edición, false = creación nueva.
 * @param {Function} onNewSurvey – Callback del botón "Nueva encuesta" (solo en creación).
 * @param {string}   shareUrl    – URL pública del survey para compartir.
 * @param {object}   t           – Objeto de traducciones.
 */
const ConfirmDialog = memo(({
  open,
  onClose,
  title,
  description,
  isEditing,
  onNewSurvey,
  shareUrl,
  t,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>

    {/* ── Encabezado ─────────────────────────────────────────────────── */}
    <DialogActions
      component="div"
      sx={{
        bgcolor: 'success.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 3,
        py: 2,
        justifyContent: 'flex-start',
      }}
    >
      <CheckCircleOutlineIcon />
      <Typography variant="h6" fontWeight={600}>
        {isEditing ? t.successTitleUpdated : t.successTitleCreated}
      </Typography>
    </DialogActions>

    {/* ── Contenido ──────────────────────────────────────────────────── */}
    <DialogContent sx={{ mt: 2, pb: 1 }}>

      {/* Tarjeta con título y descripción del survey */}
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: 'success.light',
          borderRadius: 2,
          background: 'rgba(46,125,50,0.04)',
        }}
      >
        <CardContent sx={{ pb: '12px !important' }}>
          <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
            {t.shareLinkTitle}
          </Typography>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* URL de compartir */}
      {shareUrl && (
        <Tooltip title={t.copyTooltip}>
          <TextField
            size="small"
            fullWidth
            value={shareUrl}
            variant="outlined"
            InputProps={{
              readOnly: true,
              sx: { borderRadius: 2, fontSize: '0.85rem' },
              endAdornment: (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => navigator.clipboard.writeText(shareUrl)}
                  edge="end"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </Tooltip>
      )}
    </DialogContent>

    {/* ── Acciones ───────────────────────────────────────────────────── */}
    <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'flex-end' }}>

      {/* Solo visible en modo creación */}
      {!isEditing && (
        <Button
          onClick={onNewSurvey}
          variant="outlined"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
        >
          {t.newSurveyButton}
        </Button>
      )}

      {/* Abrir survey en nueva pestaña */}
      {shareUrl && (
        <Button
          component="a"
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="success"
          startIcon={<ShareIcon />}
          onClick={onClose}
        >
          {t.viewSurveyButton}
        </Button>
      )}
    </DialogActions>
  </Dialog>
));

ConfirmDialog.displayName = 'ConfirmDialog';

export default ConfirmDialog;